import React, { useCallback, useEffect, useRef, useState } from "react";
import { Avatar } from "../../components/ui";
import { Icon } from "../shared/components";
import { getInitialTheme, toggleTheme } from "../shared/theme";

// Formata created_at em label relativo: "agora", "2 min", "1h", "3d"
function relativeTime(dateStr) {
  if (!dateStr) return "";
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (diff < 60)   return "agora";
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

const NOTIF_TYPES = {
  message:  { icon: "chat",  bg: "rgba(37,211,102,.14)",  color: "var(--brand-500)" },
  sla:      { icon: "clock", bg: "rgba(245,165,36,.14)",  color: "var(--warning)"   },
  lead:     { icon: "tag",   bg: "rgba(37,211,102,.14)",  color: "var(--brand-500)" },
  mention:  { icon: "user",  bg: "rgba(59,157,255,.14)",  color: "var(--info)"       },
  system:   { icon: "check", bg: "rgba(126,134,138,.10)", color: "var(--text-3)"    },
  campaign: { icon: "send",  bg: "rgba(167,139,250,.14)", color: "var(--purple)"    },
};

// ── Component ─────────────────────────────────────────────────────────────────
function GlobalTopBar({ api, user, activePage, setActivePage, onMenuToggle, onOpenPalette, unreadCount, logout, notifRefreshToken }) {
  const [theme, setTheme] = useState(getInitialTheme);
  const [menuOpen, setMenuOpen] = useState(false);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [activeNotifTab, setActiveNotifTab] = useState("all");
  const [loadingNotifs, setLoadingNotifs] = useState(false);

  const menuRef   = useRef(null);
  const notifRef  = useRef(null);
  const pollTimer = useRef(null);

  // ── Fetch from API ──────────────────────────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    if (!api) return;
    try {
      const res = await api.get("/notifications");
      setNotifications(res.data?.notifications || []);
    } catch {}
  }, [api]);

  // Carrega ao montar
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Re-carrega quando SSE dispara notification_created (via prop do SafeZapApp)
  useEffect(() => {
    if (notifRefreshToken > 0) fetchNotifications();
  }, [notifRefreshToken]); // eslint-disable-line

  // Polling a cada 30s (fallback quando SSE não alcança o GlobalTopBar)
  useEffect(() => {
    pollTimer.current = setInterval(fetchNotifications, 30_000);
    return () => clearInterval(pollTimer.current);
  }, [fetchNotifications]);

  // Re-carrega ao abrir o painel
  useEffect(() => {
    if (notifOpen) fetchNotifications();
  }, [notifOpen]); // eslint-disable-line

  // ── Click-outside handlers ─────────────────────────────────────────────────
  useEffect(() => {
    const onStorage = (e) => { if (e.key === "sz_theme_v1" && e.newValue) setTheme(e.newValue); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    function handle(e) { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [menuOpen]);

  useEffect(() => {
    if (!notifOpen) return;
    function handle(e) { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false); }
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, [notifOpen]);

  // ── Actions ────────────────────────────────────────────────────────────────
  async function markAllRead() {
    setNotifications((ns) => ns.map((n) => ({ ...n, read: 1 })));
    try { await api.patch("/notifications/read-all"); } catch {}
  }

  async function markRead(id) {
    setNotifications((ns) => ns.map((n) => n.id === id ? { ...n, read: 1 } : n));
    try { await api.patch(`/notifications/${id}/read`); } catch {}
  }

  // ── Derived state ──────────────────────────────────────────────────────────
  const unreadNotifs  = notifications.filter((n) => !n.read);
  const visibleNotifs = activeNotifTab === "unread" ? unreadNotifs : notifications;

  const pages = {
    dashboard:    { group: "Operação",   title: "Resumo" },
    atendimentos: { group: "Operação",   title: "Conversas" },
    contatos:     { group: "CRM",        title: "Contatos" },
    funil:        { group: "CRM",        title: "Lead" },
    tickets:      { group: "Suporte",    title: "Tickets" },
    automacoes:   { group: "Suporte",    title: "Automações" },
    chatbot:      { group: "Suporte",    title: "Chatbot" },
    supervisor:   { group: "Suporte",    title: "Supervisor" },
    campanhas:    { group: "Marketing",  title: "Campanhas" },
    conhecimento: { group: "Marketing",  title: "Base de conhecimento" },
    relatorios:   { group: "Gestão",     title: "Relatórios" },
    config:       { group: "Gestão",     title: "Configurações" },
    saas:         { group: "Plataforma", title: "Painel SaaS" },
  };
  const page = pages[activePage] || pages.dashboard;

  function navigate(pageId, payload) {
    setMenuOpen(false);
    setNotifOpen(false);
    setActivePage(pageId, payload);
  }

  return (
    <>
    {/* Modal de confirmação de logout */}
    {logoutOpen && (
      <div className="modal-overlay-v2" onClick={() => setLogoutOpen(false)}>
        <div className="modal-v2 modal-sm" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header"><h3>Sair da conta?</h3></div>
          <div className="modal-body">
            <div style={{
              display: "flex", alignItems: "flex-start", gap: 12,
              background: "var(--bg-2)", border: "1px solid var(--border)",
              borderRadius: "var(--r-4)", padding: "12px 14px",
            }}>
              <span style={{
                width: 32, height: 32, borderRadius: 8, flexShrink: 0,
                background: "rgba(59,157,255,0.12)", border: "1px solid rgba(59,157,255,0.2)",
                display: "flex", alignItems: "center", justifyContent: "center", color: "#3B9DFF",
              }}>
                <Icon name="logout" size={15} />
              </span>
              <span style={{ fontSize: 13, color: "var(--text-2)", lineHeight: 1.55 }}>
                Você precisará entrar novamente com suas credenciais para retomar de onde parou.
              </span>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-ghost btn-sm" onClick={() => setLogoutOpen(false)}>Cancelar</button>
            <button className="btn btn-primary btn-sm" onClick={() => { setLogoutOpen(false); logout?.(); }}>Sair da conta</button>
          </div>
        </div>
      </div>
    )}

    <header className="topbar">
      <button className="hamburgerBtn" onClick={onMenuToggle} aria-label="Menu">
        <span /><span /><span />
      </button>
      <div className="tb-breadcrumb">
        <span className="tb-bc-link">{page.group}</span>
        <span className="tb-bc-sep"><Icon name="chevronRight" size={11} /></span>
        <span className="tb-bc-current">{page.title}</span>
      </div>
      <div className="tb-spacer" />
      <button className="tb-search" onClick={onOpenPalette}>
        <Icon name="search" size={13} />
        <span>Buscar contatos, conversas, leads...</span>
        <span className="kbd">⌘K</span>
      </button>
      <div className="tb-actions">

        {/* ── Notifications ── */}
        <div className="tb-notif-wrap" ref={notifRef} style={{ position: "relative" }}>
          <button
            className={`tb-icon-btn${notifOpen || unreadNotifs.length > 0 ? " active" : ""}`}
            onClick={() => setNotifOpen((v) => !v)}
            title="Notificações"
          >
            <Icon name="bell" size={15} />
            {unreadNotifs.length > 0 && <span className="tb-icon-dot" />}
          </button>

          {notifOpen && (
            <div className="tb-popover" style={{ width: 380 }}>

              {/* Header */}
              <div className="tb-popover-head">
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontWeight: 700, color: "var(--text-1)", fontSize: 13 }}>Notificações</span>
                  {unreadNotifs.length > 0 && (
                    <span className="tb-notif-badge">{unreadNotifs.length} nova{unreadNotifs.length > 1 ? "s" : ""}</span>
                  )}
                </div>
                {unreadNotifs.length > 0 && (
                  <button className="tb-popover-foot-btn primary" style={{ padding: "4px 8px" }} onClick={markAllRead}>
                    ✓ Marcar todas
                  </button>
                )}
              </div>

              {/* Tabs */}
              <div className="tb-popover-tabs">
                <button
                  className={`tb-popover-tab${activeNotifTab === "all" ? " active" : ""}`}
                  onClick={() => setActiveNotifTab("all")}
                >
                  Todas <span className="count">{notifications.length}</span>
                </button>
                <button
                  className={`tb-popover-tab${activeNotifTab === "unread" ? " active" : ""}`}
                  onClick={() => setActiveNotifTab("unread")}
                >
                  Não lidas{" "}
                  <span className={`count${unreadNotifs.length > 0 ? " count-brand" : ""}`}>
                    {unreadNotifs.length}
                  </span>
                </button>
              </div>

              {/* List */}
              <div className="tb-popover-list">
                {visibleNotifs.length === 0 ? (
                  <div style={{ padding: "28px 16px", textAlign: "center", color: "var(--text-4)", fontSize: 12 }}>
                    {activeNotifTab === "unread" ? "Nenhuma notificação não lida." : "Nenhuma notificação ainda."}
                  </div>
                ) : (
                  visibleNotifs.map((n) => {
                    const cfg = NOTIF_TYPES[n.type] || NOTIF_TYPES.system;
                    return (
                      <button
                        key={n.id}
                        className={`tb-noti-item${!n.read ? " unread" : ""}`}
                        onClick={() => markRead(n.id)}
                      >
                        <div className="tb-noti-icon" style={{ background: cfg.bg, color: cfg.color }}>
                          <Icon name={cfg.icon} size={13} />
                        </div>
                        <div className="tb-noti-body">
                          <div className="tb-noti-title">{n.title}</div>
                          {n.body && <div className="tb-noti-desc">{n.body}</div>}
                        </div>
                        <div className="tb-noti-meta">
                          <span className="tb-noti-time">{relativeTime(n.created_at)}</span>
                          {!n.read && <span className="tb-noti-dot" />}
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <div className="tb-popover-foot">
                <button className="tb-popover-foot-btn" onClick={() => navigate("config", { tab: "sistema" })}>
                  <Icon name="settings" size={12} /> Preferências
                </button>
                <button className="tb-popover-foot-btn primary" onClick={() => navigate("supervisor")}>
                  Ver tudo no Supervisor <Icon name="chevronRight" size={12} />
                </button>
              </div>

            </div>
          )}
        </div>

        <button
          className="tb-icon-btn"
          onClick={() => setTheme(toggleTheme(theme))}
          title={theme === "dark" ? "Tema claro" : "Tema escuro"}
        >
          <Icon name={theme === "dark" ? "sun" : "moon"} size={15} />
        </button>

        {/* Avatar + dropdown */}
        <div className="tb-user-wrap" ref={menuRef}>
          <button
            className={`tb-user${menuOpen ? " tb-user--open" : ""}`}
            onClick={() => setMenuOpen((v) => !v)}
            aria-haspopup="true"
            aria-expanded={menuOpen}
          >
            <Avatar name={user.name} size="sm" />
          </button>

          {menuOpen && (
            <div className="tb-user-menu" role="menu">
              <div className="tb-um-head">
                <Avatar name={user.name} size="md" />
                <div className="tb-um-info">
                  <span className="tb-um-name">{user.name}</span>
                  <span className="tb-um-email">{user.email}</span>
                </div>
              </div>
              <div className="tb-um-divider" />
              <button className="tb-um-item" role="menuitem" onClick={() => navigate("config", { tab: "equipe" })}>
                <span className="tb-um-icon"><Icon name="user" size={14} /></span>
                Perfil
              </button>
              <button className="tb-um-item" role="menuitem" onClick={() => navigate("config", { tab: "sistema" })}>
                <span className="tb-um-icon"><Icon name="settings" size={14} /></span>
                Preferências
              </button>
              <div className="tb-um-divider" />
              <button className="tb-um-item tb-um-item--danger" role="menuitem" onClick={() => { setMenuOpen(false); setLogoutOpen(true); }}>
                <span className="tb-um-icon"><Icon name="logout" size={14} /></span>
                Sair
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
    </>
  );
}

export default GlobalTopBar;
